import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { GcpPricingDatabase, GcpSize } from "@/lib/api/cloud";

/** The six steps, in order, and the route each lives at (under /connections/google-cloud). */
export const GCP_WIZARD_STEPS = [
  { id: "start", label: "Start", path: "" },
  { id: "sign-in", label: "Sign in with Google", path: "sign-in" },
  { id: "project", label: "Project and billing", path: "project" },
  { id: "plan", label: "Plan and cost", path: "plan" },
  { id: "build", label: "Build", path: "build" },
  { id: "live", label: "Live", path: "live" },
] as const;
export type GcpWizardStepId = (typeof GCP_WIZARD_STEPS)[number]["id"];

export const GCP_WIZARD_BASE = "/connections/google-cloud";
export const DEFAULT_BUDGET_USD = 25;

export function gcpWizardPath(step: GcpWizardStepId): string {
  const found = GCP_WIZARD_STEPS.find((s) => s.id === step);
  return found && found.path ? `${GCP_WIZARD_BASE}/${found.path}` : GCP_WIZARD_BASE;
}

export interface GcpWizardState {
  /** The sealed Google sign-in; null until the admin has signed in. */
  session: string | null;
  projectMode: "new" | "existing";
  projectId: string;
  billingAccountId: string;
  region: string;
  database: GcpPricingDatabase;
  size: GcpSize;
  budgetUsd: number;
}

export const INITIAL_GCP_WIZARD_STATE: GcpWizardState = {
  session: null,
  projectMode: "new",
  projectId: "",
  billingAccountId: "",
  region: "us-central1",
  database: "cloudsql",
  size: "starter",
  budgetUsd: DEFAULT_BUDGET_USD,
};

const STORAGE_KEY = "compbox.gcpWizard";

/** The wizard's choices survive the round trip to Google (and a reload) in this tab's session storage. */
export function readGcpWizardState(): GcpWizardState {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_GCP_WIZARD_STATE;
    return { ...INITIAL_GCP_WIZARD_STATE, ...(JSON.parse(raw) as Partial<GcpWizardState>) };
  } catch {
    return INITIAL_GCP_WIZARD_STATE;
  }
}

export function writeGcpWizardState(patch: Partial<GcpWizardState>): GcpWizardState {
  const next = { ...readGcpWizardState(), ...patch };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage can be blocked; the wizard still works for this page view.
  }
  return next;
}

export function clearGcpWizardState(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface WizardContextValue {
  state: GcpWizardState;
  update: (patch: Partial<GcpWizardState>) => void;
  reset: () => void;
}

const WizardContext = createContext<WizardContextValue | null>(null);

export function GcpWizardProvider({ children, initial }: { children: ReactNode; initial?: Partial<GcpWizardState> }) {
  const [state, setState] = useState<GcpWizardState>(() => ({ ...readGcpWizardState(), ...initial }));
  const update = useCallback((patch: Partial<GcpWizardState>) => setState(writeGcpWizardState(patch)), []);
  const reset = useCallback(() => {
    clearGcpWizardState();
    setState(INITIAL_GCP_WIZARD_STATE);
  }, []);
  const value = useMemo(() => ({ state, update, reset }), [state, update, reset]);
  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useGcpWizard(): WizardContextValue {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useGcpWizard must be used inside GcpWizardProvider");
  return ctx;
}
